import { useCallback, useEffect, useRef, useState } from 'react';
import { getHighlightJob } from '../api';
import type { RttsJobDetail } from '../types';

export const RTTS_JOB_POLL_INTERVAL_MS = 4000;

function isTerminalStatus(status: string | undefined | null): boolean {
  return status === 'delivered' || status === 'failed';
}

export type UsePollJobOptions = {
  leagueId: string;
  jobId: string | null;
  /** When false, the hook idles without polling. Default true when jobId is set. */
  enabled?: boolean;
  pollIntervalMs?: number;
};

export type UsePollJobResult = {
  status: string | null;
  job: RttsJobDetail | null;
  error: string | null;
  isPolling: boolean;
  /** One-shot refresh (e.g. signed URL expiry). Does not require polling to be active. */
  refresh: () => Promise<RttsJobDetail | null>;
};

/**
 * Poll highlight job status every ~4s until delivered or failed.
 * Guards overlapping requests and tears down the interval on unmount / terminal.
 */
export function usePollJob({
  leagueId,
  jobId,
  enabled = true,
  pollIntervalMs = RTTS_JOB_POLL_INTERVAL_MS,
}: UsePollJobOptions): UsePollJobResult {
  const [job, setJob] = useState<RttsJobDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const inFlightRef = useRef(false);
  const cancelledRef = useRef(false);

  const refresh = useCallback(async (): Promise<RttsJobDetail | null> => {
    if (!leagueId || !jobId) return null;
    if (inFlightRef.current) return null;
    inFlightRef.current = true;
    try {
      const next = await getHighlightJob(leagueId, jobId);
      if (!cancelledRef.current) {
        setJob(next);
        setError(null);
      }
      return next;
    } catch (err) {
      if (!cancelledRef.current) {
        setError(err instanceof Error ? err.message : 'Could not refresh reel status.');
      }
      return null;
    } finally {
      inFlightRef.current = false;
    }
  }, [leagueId, jobId]);

  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
    };
  }, []);

  useEffect(() => {
    setJob(null);
    setError(null);
    setIsPolling(false);
  }, [leagueId, jobId]);

  useEffect(() => {
    if (!enabled || !leagueId || !jobId) {
      setIsPolling(false);
      return undefined;
    }

    let stopped = false;
    setIsPolling(true);

    const tick = async () => {
      if (stopped || cancelledRef.current || inFlightRef.current) return;
      const next = await refresh();
      if (stopped || cancelledRef.current) return;
      if (next && isTerminalStatus(String(next.status))) {
        setIsPolling(false);
        stopped = true;
      }
    };

    void tick();
    const intervalId = window.setInterval(() => {
      void tick();
    }, pollIntervalMs);

    return () => {
      stopped = true;
      window.clearInterval(intervalId);
      setIsPolling(false);
    };
  }, [enabled, leagueId, jobId, pollIntervalMs, refresh]);

  // Stop polling once we observe a terminal job (e.g. from an external refresh).
  useEffect(() => {
    if (job && isTerminalStatus(String(job.status))) {
      setIsPolling(false);
    }
  }, [job]);

  return {
    status: job ? String(job.status) : null,
    job,
    error,
    isPolling,
    refresh,
  };
}
